import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import {
  formTemplateService,
  openPdfBlob,
  TemplateSummary,
  TemplateType,
  UploadResult,
} from '../../../services/formTemplate.service';
import { MappingCounts, StatusBadge, TemplateTypeLabel } from './shared';
import { ServiceProviderPicker } from './ServiceProviderPicker';
import { useConfirm } from './useConfirm';
import './FormTemplates.scss';

const TEMPLATE_TYPES: TemplateType[] = ['BOOKLET', 'CKYC', 'FATCA', 'OTHER'];

/**
 * Admin — service-provider PDF templates: list, upload (automated discover + library
 * check + auto-match), open the mapping screen, compose booklets, retire/delete.
 * Rendered inside MainLayout like the rest of the admin console.
 */
export const FormTemplateList: React.FC = () => {
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirm();
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [spFilter, setSpFilter] = useState('');

  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [spRef, setSpRef] = useState('');
  const [spName, setSpName] = useState('');
  const [spType, setSpType] = useState('');
  const [name, setName] = useState('');
  const [templateType, setTemplateType] = useState<TemplateType>('BOOKLET');
  const [file, setFile] = useState<File | null>(null);
  const [lastResult, setLastResult] = useState<UploadResult | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setTemplates(await formTemplateService.list(spFilter.trim() || undefined));
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [spFilter]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!spRef.trim() || !file) {
      toast.warn('Choose a service provider and a PDF file');
      return;
    }
    try {
      setUploading(true);
      toast.info('Uploading and discovering fields, please wait…');
      const result = await formTemplateService.upload({
        spRef: spRef.trim(), spName: spName.trim() || undefined, spType: spType || undefined,
        name: name.trim() || undefined, templateType, file,
      });
      setLastResult(result);
      toast.success(result.message);
      setFile(null);
      setName('');
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleActivate = async (t: TemplateSummary) => {
    const ok = await confirm('Activate template',
      `Activate "${t.name}" v${t.version} for ${t.spName || t.spRef}? Any previously active template of the same type is retired, and investors of this provider get this form from now on.`,
      'Activate');
    if (!ok) return;
    try {
      await formTemplateService.activate(t.id);
      toast.success(`${t.name} v${t.version} is now ACTIVE`);
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Activation failed');
    }
  };

  const handleRetire = async (t: TemplateSummary) => {
    const ok = await confirm('Deactivate template',
      `Deactivate "${t.name}" v${t.version}? Investors of ${t.spName || t.spRef} will have no account-opening booklet to download until another version is activated.`,
      'Deactivate');
    if (!ok) return;
    try {
      await formTemplateService.retire(t.id);
      toast.success('Template deactivated — no booklet is published for this provider now');
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Retire failed');
    }
  };

  const handleDelete = async (t: TemplateSummary) => {
    const ok = await confirm('Delete template',
      `Delete "${t.name}" v${t.version} and all its field mappings? This cannot be undone.`, 'Delete');
    if (!ok) return;
    try {
      await formTemplateService.delete(t.id);
      toast.success('Template deleted');
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleOpenPdf = async (t: TemplateSummary) => {
    try {
      openPdfBlob(await formTemplateService.templatePdf(t.id), t.originalFilename || `template-${t.id}.pdf`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not open the PDF');
    }
  };

  return (
    <div className="form-templates">
      {dialog}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div>
          <h1 className="dashboard-title-modern mb-0">SP Form Templates</h1>
          <div className="text-muted small">
            Upload a service provider's PDF forms, map their fields to investor data and activate them.
            Investors of that provider download the forms pre-filled. A provider without an active booklet has nothing to download until one is published here.
          </div>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/admin/form-templates/compose')}>
            <i className="bi bi-collection me-1" /> Compose booklet
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowUpload((v) => !v)}>
            <i className="bi bi-upload me-1" /> Upload PDF
          </button>
        </div>
      </div>

      {showUpload && (
        <form className="card p-3 mb-3" onSubmit={handleUpload}>
          <h2 className="form-templates__h2">Upload a service provider PDF</h2>
          <div className="row g-2 align-items-end">
            <div className="col-md-5">
              <ServiceProviderPicker value={spRef} required
                onChange={(o) => { setSpRef(o?.spRef || ''); setSpName(o?.spName || ''); setSpType(o?.spType || ''); }} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Template name</label>
              <input className="form-control form-control-sm" value={name} onChange={(e) => setName(e.target.value)} placeholder="Defaults to the file name" />
            </div>
            <div className="col-md-3">
              <label className="form-label">Template type</label>
              <select className="form-select form-select-sm" value={templateType} onChange={(e) => setTemplateType(e.target.value as TemplateType)}>
                {TEMPLATE_TYPES.map((t) => <option key={t} value={t}>{TemplateTypeLabel[t]}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">PDF file <span className="text-danger">*</span></label>
              <input className="form-control form-control-sm" type="file" accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)} required />
            </div>
            <div className="col-md-6 d-flex gap-2 justify-content-end">
              <button type="button" className="btn btn-link btn-sm" onClick={() => setShowUpload(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={uploading}>
                {uploading ? 'Processing…' : 'Upload & discover'}
              </button>
            </div>
          </div>
          <div className="text-muted small mt-2">
            After upload the system stores the PDF, discovers every form field, checks which pages already exist in the
            library (CKYC, FATCA) and auto-matches field names. Only what it cannot match is flagged for you.
          </div>
        </form>
      )}

      {lastResult && (
        <div className="card p-3 mb-3 border-start border-4 border-success">
          <h2 className="form-templates__h2">Last upload</h2>
          <p className="mb-2">{lastResult.message}</p>
          <ul className="mb-2 small">
            {lastResult.libraryCheck.map((l) => (
              <li key={l.code}>
                <strong>{l.displayName}</strong>: {l.overlap}/{l.inventorySize} fields
                {l.recognised ? ` — recognised, ${l.seededFields} pre-mapped` : ' — not recognised'}
              </li>
            ))}
          </ul>
          <div>
            <button className="btn btn-primary btn-sm" onClick={() => navigate(`/admin/form-templates/${lastResult.template.id}`)}>
              Open mapping screen
            </button>
          </div>
        </div>
      )}

      <div className="card p-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <h2 className="form-templates__h2 mb-0">Templates</h2>
            <span className="text-muted small">
              Mapping: <span className="badge bg-success-subtle text-success">confirmed</span>{' '}
              <span className="badge bg-primary-subtle text-primary">auto-matched</span>{' '}
              <span className="badge bg-danger-subtle text-danger">flagged</span>{' '}
              <span className="badge bg-light text-muted">not applicable</span>
            </span>
          </div>
          <input className="form-control form-control-sm form-templates__search" placeholder="Filter by SP reference"
            value={spFilter} onChange={(e) => setSpFilter(e.target.value)} />
        </div>
        {loading ? <LoadingSpinner /> : (
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Service provider</th>
                  <th>Template</th>
                  <th>Type</th>
                  <th>Ver</th>
                  <th>Status</th>
                  <th>Pages / fields</th>
                  <th>Mapping</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {templates.length === 0 && (
                  <tr><td colSpan={8} className="text-muted">No templates yet. Upload a service provider PDF to begin.</td></tr>
                )}
                {templates.map((t) => (
                  <tr key={t.id}>
                    <td>{t.spName || t.spRef}<br /><small className="text-muted">{t.spRef}</small></td>
                    <td>
                      {t.name}<br />
                      <small className="text-muted">
                        {t.assembled ? 'Assembled booklet' : t.originalFilename}
                        {t.recognisedLibrary ? ` · library: ${t.recognisedLibrary.split(',').join(', ')}` : ''}
                        {t.usedInBooklets?.length ? ` · part of ${t.usedInBooklets.join(', ')}` : ''}
                      </small>
                    </td>
                    <td>{TemplateTypeLabel[t.templateType]}</td>
                    <td>v{t.version}</td>
                    <td><StatusBadge status={t.status} /></td>
                    <td>{t.pageCount ?? '—'} / {t.fieldCount ?? '—'}</td>
                    <td><MappingCounts t={t} /></td>
                    <td className="text-nowrap">
                      {t.status !== 'FLAT' && (
                        <button className="btn btn-sm btn-primary me-1" onClick={() => navigate(`/admin/form-templates/${t.id}`)}>
                          {t.status === 'ACTIVE' || t.status === 'RETIRED' ? 'View' : 'Map'}
                        </button>
                      )}
                      <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => handleOpenPdf(t)} title="Open blank PDF">
                        <i className="bi bi-file-earmark-pdf" />
                      </button>
                      {['DISCOVERED', 'MAPPING_IN_PROGRESS', 'VALIDATED'].includes(t.status) && (
                        <button className="btn btn-sm btn-success me-1" onClick={() => handleActivate(t)}
                          disabled={t.flagged > 0 || (t.usedInBooklets?.length ?? 0) > 0}
                          title={(t.usedInBooklets?.length ?? 0) > 0
                            ? `Part of ${t.usedInBooklets.join(', ')} — activate that booklet instead`
                            : t.flagged > 0 ? `${t.flagged} field(s) still flagged — finish mapping first` : 'Make this the live template for the provider'}>
                          Activate
                        </button>
                      )}
                      {t.status === 'ACTIVE' ? (
                        <>
                          <span className="btn btn-sm btn-success me-1 pe-none" aria-disabled="true">
                            <i className="bi bi-check-circle-fill me-1" />Live for investors
                          </span>
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => handleRetire(t)}
                            title="Stop using this template — investors fall back to the platform default">
                            Deactivate
                          </button>
                        </>
                      ) : (
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(t)}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
